import { useInfiniteQuery } from '@tanstack/react-query';
import { getPlotEventsTimeline } from '../api/sitesApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { PlotEventsTimelineResponse } from '../types/plotTimeline';

export const plotTimelineKeys = {
  all: ['plotTimeline'] as const,
  lists: () => [...plotTimelineKeys.all, 'list'] as const,
  list: (siteId: number, plotId: number, isAuthenticated: boolean) =>
    [...plotTimelineKeys.lists(), { siteId, plotId, isAuthenticated }] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UsePlotEventsTimelineReturn {
  events: PlotEventsTimelineResponse['items'];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch plot events timeline with infinite scroll pagination
 */
export function usePlotEventsTimeline(
  siteId: number | null,
  plotId: number | null
): UsePlotEventsTimelineReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: plotTimelineKeys.list(siteId ?? 0, plotId ?? 0, isAuthenticated),
    queryFn: async ({ pageParam = 0 }) => {
      if (!siteId || !plotId) {
        throw new Error('siteId and plotId are required');
      }

      return await getPlotEventsTimeline(siteId, plotId, {
        skip: pageParam,
        take: ITEMS_PER_PAGE,
      });
    },
    getNextPageParam: lastPage => {
      return lastPage?.hasMore ? lastPage.skip + lastPage.take : undefined;
    },
    initialPageParam: 0,
    enabled:
      isAuthenticated &&
      siteId !== null &&
      siteId > 0 &&
      plotId !== null &&
      plotId > 0,
    staleTime: 2 * 60 * 1000,
  });

  const events = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    events,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
